<template>
    <div class="card" style="min-height: 200px">
        <div class="card-body">

            <div class="row mb-3 align-items-end">
                <div class="col-md-4">
                    <label class="form-label">Application Type</label>
                    <select v-model="params.application_type" class="form-select">
                        <option value="">All Types</option>
                        <option v-for="(label, key) in applicationTypes" :key="key" :value="key">{{ label }}</option>
                    </select>
                </div>
                <div class="col-md-4">
                    <label class="form-label">Mobile / Tracking No</label>
                    <input
                        v-model="params.search"
                        type="text"
                        class="form-control"
                        placeholder="Search by mobile or tracking no"
                    />
                </div>
                <div class="col-md-2">
                    <button class="btn btn-primary" @click="onClickGo">Go</button>
                </div>
            </div>

            <div class="table-responsive">
                <table class="table table-bordered table-hover">
                    <thead class="table-light">
                        <tr>
                            <th>SL</th>
                            <th>Tracking No</th>
                            <th>Name</th>
                            <th>Mobile</th>
                            <th>Application Type</th>
                            <th>Date</th>
                            <th class="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="7" class="text-center py-4">Loading...</td>
                        </tr>
                        <tr v-else-if="!list.data || list.data.length === 0">
                            <td colspan="7" class="text-center py-4">No applications found.</td>
                        </tr>
                        <tr v-for="(item, i) in list.data" :key="item.id">
                            <td>{{ i + 1 + ((list.current_page - 1) * parseInt(list.per_page)) }}</td>
                            <td>{{ item.tracking_no }}</td>
                            <td>{{ item.name }}</td>
                            <td>{{ item.mobile }}</td>
                            <td>{{ applicationTypes[item.application_type] || item.application_type }}</td>
                            <td>{{ item.created_at ? item.created_at.slice(0, 10) : '' }}</td>
                            <td class="text-center">
                                <button class="btn btn-sm btn-primary" @click="openModal(item.id)">
                                    <i class="fas fa-eye"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="mt-3">
                <Pagination :offset="params.offset" @onPageChange="onPageChange" />
            </div>
        </div>
    </div>

    <ApplicationDetailModal
        v-model="modalOpen"
        :application-id="selectedId"
        @close="closeModal"
        @accepted="onActionDone"
        @rejected="onActionDone"
    />
</template>

<script setup>
import Pagination from '@/components/atom/Pagination.vue'
import ApplicationDetailModal from '@/views/page/wasa/ApplicationDetailModal.vue'
import handleWaterSupply from '@/services/modules/waterSupply'
import { ref, computed, inject, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'

const store = useStore()
const route = useRoute()
const router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const { fetchPendingGeneralApplications, fetchApplicationTypes } = handleWaterSupply()

const companyId = computed(() => route.params.companyId)

const loading = ref(false)
const list = ref({ data: [], current_page: 1, per_page: 20 })
const applicationTypes = ref({})
const modalOpen = ref(false)
const selectedId = ref(null)

const params = ref({
    offset: 20,
    application_type: '',
    search: '',
})

const getQuery = () => {
    let q = '?company_id=' + companyId.value + '&offset=' + params.value.offset
    if (route.query.page) q += '&page=' + route.query.page
    if (params.value.application_type) q += '&application_type=' + params.value.application_type
    if (params.value.search) q += '&search=' + encodeURIComponent(params.value.search)
    return q
}

const setPagination = (data) => {
    store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const loadList = async () => {
    loading.value = true
    list.value = { data: [], current_page: 1, per_page: params.value.offset }
    await fetchPendingGeneralApplications(getQuery()).then(res => {
        if (!res.status) {
            store.commit('resetPagination')
            return
        }
        list.value = res.data
        setPagination(res.data)
    }).finally(() => {
        loading.value = false
    })
}

const onPageChange = async (page) => {
    const q = Object.assign({}, route.query, { page })
    await router.push({ path: route.path, query: q })
    await loadList()
}

const onClickGo = async () => {
    const q = Object.assign({}, route.query, { page: 1 })
    await router.push({ path: route.path, query: q })
    await loadList()
}

const openModal = (id) => {
    selectedId.value = id
    modalOpen.value = true
}

const closeModal = () => {
    modalOpen.value = false
    selectedId.value = null
}

const onActionDone = (message) => {
    showSuccess(message)
    closeModal()
    loadList()
}

onMounted(async () => {
    await fetchApplicationTypes().then(res => {
        if (res.status) applicationTypes.value = res.data
    })
    await loadList()
})
</script>
